import { IconBolt } from '@tabler/icons-react';
import { DockPanel, useDock } from '@hana/shell';
import { Bars, Kpis, Notis, Sec, type DockNoti } from './crmDock';
import { STAGE_LABEL, STAGE_ORDER, isOpenDeal } from './logic';
import type { Deal, DealStage } from './types';
import { useT } from '@hana/i18n';
import './i18n';

// Bảng phải của Mint-Pipeline (khổ 1920 mở sẵn): Theo sale · Tự động · Thông báo. Số liệu tính từ danh sách phiếu; Tự động / Thông báo là mẫu của board.
type K = 'stat' | 'auto' | 'noti';
const STAGE_C: Record<DealStage, string> = { New: 'var(--d-gray)', Pricing: 'var(--d-amber)', Quoted: 'var(--d-blue)', Won: 'var(--d-green)', Lost: 'var(--line)' };
const SALES = ['DAVID', 'TUANNT', 'ANDG', 'AnNPH'];
const LOST_REASONS = ['Giá', 'Lịch tàu', 'Dịch vụ', 'Khách huỷ', 'Khác'];
const RULES: Array<[string, string]> = [
  ['Gửi mail báo giá cước', 'Phiếu đang mở cùng Ref → Đã báo; chưa có phiếu thì tạo phiếu Đã báo'],
  ['Gửi báo giá dịch vụ (TRF, CCF…)', 'Phiếu mở gần nhất cùng nhóm dịch vụ → Đã báo, ghi RefCode'],
  ['Sale hỏi Pricing', 'Phiếu Mới → Chờ giá'],
  ['Job chọn Ref báo giá', 'Phiếu tự chốt Won kèm JobID'],
  ['Đặt / dời / xoá ngày follow', 'Tự sinh / dời / đóng việc follow ở Việc & Duyệt'],
];
const AUTO_LOG: DockNoti[] = [
  { text: 'Gửi mail Q26FRSGNNSA201 → SI-261022-QW7N Đã báo', when: '22/Oct/2026 09:05', tone: 'gray' },
  { text: 'Hỏi Pricing PR-261021-094620-K8 → SI-261021-3MPA Chờ giá', when: '21/Oct/2026 09:46', tone: 'gray' },
  { text: 'Job SE260639171 chọn Ref → SI-261012-X7JD Won', when: '20/Oct/2026 14:30', tone: 'gray' },
  { text: 'Cước SINOKOR đổi → SI-261019-Z2HD Giá đổi', when: '20/Oct/2026 08:00', tone: 'gray' },
];
const NOTIS: DockNoti[] = [
  { text: 'Pricing trả giá PR-261021-094620-K8 · THACO', when: '10 phút trước', tone: 'blue', unread: true },
  { text: 'Pricing bỏ qua PR-261015-142210-Q3 · BAOANVISION', when: '1 giờ trước', tone: 'red', unread: true },
  { text: 'Giá đổi làm thay đổi báo giá Q26FDSGNPUS199', when: 'Hôm qua 08:00', tone: 'gray' },
];

export function usePipelineDock() {
  return useDock<K>('stat');
}

export function PipelineDock({ rows, dock }: { rows: Deal[]; dock: ReturnType<typeof usePipelineDock> }) {
  const t = useT();
  if (!dock.open) return null;
  const open = rows.filter(isOpenDeal);
  const won = rows.filter((d) => d.stage === 'Won').length;
  const lost = rows.filter((d) => d.stage === 'Lost');
  const pct = (a: number, b: number) => (a + b ? Math.round((a / (a + b)) * 100) : 0);
  const funnel = STAGE_ORDER.map((sg) => ({ k: STAGE_LABEL[sg], n: rows.filter((d) => d.stage === sg).length, c: STAGE_C[sg] }));
  const quoted = funnel[2]!.n;
  const bySale = SALES.map((k) => {
    const a = open.filter((d) => d.sale === k);
    const c = (sg: DealStage) => a.filter((d) => d.stage === sg).length;
    return { k, n: a.length, text: `${c('New')} / ${c('Pricing')} / ${c('Quoted')}` };
  });
  const lostBy = LOST_REASONS.map((k) => ({ k, n: lost.filter((d) => d.closeNote.startsWith(k)).length })).filter((r) => r.n > 0);
  const tabs = [
    { key: 'stat' as const, label: 'Theo sale' },
    { key: 'auto' as const, label: 'Tự động' },
    { key: 'noti' as const, label: 'Thông báo' },
  ];
  return (
    <DockPanel tabs={tabs} tab={dock.tab} onTab={dock.show} onClose={dock.close} closeLabel="Đóng cột phải">
      {dock.tab === 'stat' && (
        <>
          <Sec title={t('crm.at.89')}>
            <Bars rows={funnel} h={22} cols="58px minmax(0,1fr) 30px" label={(r) => `${r.k}: ${r.n} phiếu`} />
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--lb)' }}>
              {open.length} phiếu đang mở · Đã báo → Won: {pct(won, quoted)}% · đếm phiếu, không tính tiền
            </div>
          </Sec>
          <Sec title="Pipeline theo sale">
            <div style={{ marginBottom: 8, fontSize: 12, color: 'var(--lb)' }}>{t('crm.dk.openNote')}</div>
            <Bars rows={bySale} h={30} cols="70px minmax(0,1fr) 70px" label={(r) => `${r.k}: ${r.n} phiếu mở`} />
          </Sec>
          <Sec title={t('crm.at.90')}>
            <Kpis items={[{ k: 'Won', v: String(won) }, { k: 'Lost', v: String(lost.length) }, { k: 'Tỷ lệ chốt Won', v: `${pct(won, lost.length)}%` }]} />
            <div style={{ margin: '10px 0 4px', fontSize: 12, fontWeight: 550, color: 'var(--tx2)' }}>{t('crm.dk.lostWhy')}</div>
            <Bars rows={lostBy} h={18} cols="64px minmax(0,1fr) 20px" label={(r) => `Lý do Lost ${r.k}: ${r.n}`} />
          </Sec>
        </>
      )}
      {dock.tab === 'auto' && (
        <>
          <Sec title={t('crm.at.91')} icon={<IconBolt size={16} aria-hidden="true" />}>
            {RULES.map(([k, d], i) => (
              <div key={k} style={{ padding: '7px 0', borderTop: i ? '1px solid var(--line)' : undefined }}>
                <b style={{ fontWeight: 550 }}>{k}</b>
                <div style={{ fontSize: 12, color: 'var(--lb)' }}>{d}</div>
              </div>
            ))}
          </Sec>
          <h2 style={{ margin: '4px 0 0', fontSize: 14, fontWeight: 600 }}>{t('crm.dk.autoDone')}</h2>
          <Notis items={AUTO_LOG} />
        </>
      )}
      {dock.tab === 'noti' && <Notis items={NOTIS} />}
    </DockPanel>
  );
}
